import { Dispatch, ElementType, SetStateAction } from 'react';
import { DynamicField, TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { TemplatorV2Queries } from '@frontend/api-templator-v2';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Button,
  Heading,
  KeyNames,
  MessageTemplate,
  PolymorphicComponentPropWithoutRef,
  TagType,
  Text,
} from '@frontend/design-system';
import { ManualTemplateTypes } from '../../types';

type MultiSendViewProps = {
  groupIds: string[];
  contactCount: number;
  templateTypes: ManualTemplateTypes[];
  initialTemplateString?: string;
  templateString: string;
  setTemplateString: Dispatch<SetStateAction<string>>;
  openTemplateSelector?: () => void;
  onSubmit?: () => void;
  templateEditorKey?: string;
};

type PolymorphicProps<E extends ElementType> = PolymorphicComponentPropWithoutRef<E, MultiSendViewProps>;

export const MultiSendView = <E extends ElementType = 'div'>({
  as,
  groupIds,
  contactCount,
  templateTypes,
  initialTemplateString,
  templateString,
  setTemplateString,
  openTemplateSelector,
  children,
  onSubmit,
  templateEditorKey,
  ...rest
}: PolymorphicProps<E>) => {
  const { t } = useTranslation('integrated-messaging');
  const Component = as || 'div';
  const { data: dynamicFields } = TemplatorV2Queries.useTemplateTypesList(
    {
      businessGroupIds: groupIds,
      templateTypeSlugs: templateTypes,
    },
    {
      select: (data) =>
        data.templateTypes.reduce<DynamicField[]>((acc, templateType) => {
          if ((templateTypes as TemplateType_Slug[]).includes(templateType.slug)) {
            templateType.dynamicFields.forEach((dynamicField) => {
              const dynamicFieldAlreadyInAcc = acc.some((entry) => entry.key === dynamicField.key);
              if (!dynamicFieldAlreadyInAcc) {
                acc.push(dynamicField);
              }
            });
          }
          return acc;
        }, []),
    }
  );
  const tags =
    dynamicFields?.reduce<TagType[]>((acc, dynamicField) => {
      const { label, key, exampleValues } = dynamicField;
      if (label && key) {
        acc.push({
          label,
          key: `{{${key}}}`,
          value: exampleValues?.[0] ?? '',
          optional: !dynamicField.isRequired,
          nonRemovable: !!dynamicField.isRequired,
        });
      }
      return acc;
    }, []) ?? [];

  return (
    <Component {...rest}>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: theme.spacing(2),
          padding: theme.spacing(2),
          borderBottom: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <div
          css={{
            minWidth: 'max-content',
            paddingRight: theme.spacing(8),
          }}
        >
          <Text color='light' size='medium'>
            {t('Sending to')}
          </Text>
          <Heading level={3}>{t('{{count}} Contacts', { count: contactCount })}</Heading>
        </div>
        {children}
      </div>
      <MessageTemplate
        onChange={(templateString) => setTemplateString(templateString)}
        tags={tags}
        initialTemplate={initialTemplateString}
        hasDynamicTags
        draggableTags
        isNewTag
        key={templateEditorKey}
      >
        <MessageTemplate.Editor
          showEmojiPicker
          css={{
            minHeight: '200px',
            border: 'none',
          }}
          onKeyDownCapture={(e) => {
            if (e.key === KeyNames.Enter && !e.shiftKey && !!onSubmit) {
              e.preventDefault();
              e.stopPropagation();
              onSubmit();
            }
          }}
        >
          {openTemplateSelector !== undefined && (
            <Button iconName='template' variant='tertiary' size='large' onClick={openTemplateSelector}>
              {t('Select a Template')}
            </Button>
          )}
        </MessageTemplate.Editor>
      </MessageTemplate>
    </Component>
  );
};
